import React from 'react'
import { createPortal } from 'react-dom'
import { Button, Heading, LoadingSpinner, Text } from '../ui'
import { supportCenterAttendService } from '../../services/supportCenterAttend'
import { supportCenterReportsService } from '../../services/supportCenterReports'
import type {
  SupportCenterAppointmentRecord,
  SupportCenterHistoryItem,
} from '../../types/supportCenterReports'
import { APPOINTMENT_STATUS_LABELS } from '../../types/supportCenterReports'
import { attendanceNotesLabel, isReducedTriageForm } from '../../types/supportCenterTriage'
import { appointmentChannelLabel } from '../../types/supportCenterAppointments'
import {
  SUPPORT_CENTER_PSYCH_VIEW_FIELDS,
  type SupportCenterAttendanceNotesRecord,
  type SupportCenterPsychAttendanceRecord,
} from '../../types/supportCenterPsychAttendance'
import {
  appointmentStatusBadgeClass,
  formatCpf,
  formatDateTime,
  formatPhone,
} from './dashboardHelpers'

function display(value: string | null | undefined): string {
  const text = value?.trim()
  return text ? text : '—'
}

function displayDate(value: string | null | undefined): string {
  if (!value?.trim()) return '—'
  return formatDateTime(value)
}

function joinList(values: string[]): string {
  const items = values.map((value) => value.trim()).filter(Boolean)
  return items.length ? items.join(', ') : '—'
}

function formatDateBr(isoDate: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(isoDate)) return isoDate
  const [y, m, d] = isoDate.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

/** Soft-delete incompleto (deleted_at + marcado/reagendado) conta como cancelado, igual à lista. */
function appointmentStatusForDisplay(
  status: SupportCenterAppointmentRecord['status'],
  deletedAt: string | null
): SupportCenterAppointmentRecord['status'] {
  if (deletedAt && (status === 'marcado' || status === 'reagendado')) return 'cancelado'
  return status
}

function DetailField({
  label,
  value,
  wide,
}: {
  label: string
  value: React.ReactNode
  wide?: boolean
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
        {value || '—'}
      </dd>
    </div>
  )
}

function DetailSection({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <section className="space-y-3">
      <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
        {title}
      </Heading>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">{children}</dl>
    </section>
  )
}

function renderDemandList(
  items:
    | Array<{
        id: string
        name: string
        notes?: string | null
      }>
    | null
    | undefined
): React.ReactNode {
  const rows = (items ?? []).filter((item) => item.name?.trim())
  if (rows.length === 0) return '—'
  return (
    <div className="space-y-1">
      {rows.map((item) => (
        <div key={item.id}>
          {item.name}
          {item.notes?.trim() ? `: ${item.notes.trim()}` : ''}
        </div>
      ))}
    </div>
  )
}

type AppointmentRecordModalProps = {
  record: SupportCenterAppointmentRecord
  onClose: () => void
}

export const AppointmentRecordModal: React.FC<AppointmentRecordModalProps> = ({
  record: initialRecord,
  onClose,
}) => {
  const [record, setRecord] = React.useState(initialRecord)
  const [recordLoading, setRecordLoading] = React.useState(false)
  const [recordError, setRecordError] = React.useState<string | null>(null)

  const [timeline, setTimeline] = React.useState<SupportCenterHistoryItem[]>([])
  const [timelineLoading, setTimelineLoading] = React.useState(false)
  const [timelineError, setTimelineError] = React.useState<string | null>(null)

  const [psychAttendance, setPsychAttendance] =
    React.useState<SupportCenterPsychAttendanceRecord | null>(null)
  const [attendanceNotes, setAttendanceNotes] =
    React.useState<SupportCenterAttendanceNotesRecord | null>(null)
  const [formLoading, setFormLoading] = React.useState(false)
  const [formError, setFormError] = React.useState<string | null>(null)

  const patientCpfDigits = onlyDigits(record.patient_cpf)
  const formKind = record.form_kind
  const isPsychological = formKind === 'psicologico'
  const isReducedForm = isReducedTriageForm(formKind)
  const notesLabel = attendanceNotesLabel(formKind)
  const displayStatus = appointmentStatusForDisplay(record.status, record.deleted_at)
  const statusLabel = APPOINTMENT_STATUS_LABELS[displayStatus] || displayStatus
  const coverage =
    record.assistance_name && record.assistance_code
      ? `${record.assistance_name} (${record.assistance_code})`
      : record.assistance_name || record.assistance_code
  const attendanceDemandList = renderDemandList(
    psychAttendance?.demand_types ?? attendanceNotes?.demand_types
  )

  React.useEffect(() => {
    setRecord(initialRecord)
  }, [initialRecord])

  React.useEffect(() => {
    if (patientCpfDigits.length !== 11) {
      setTimeline([])
      setTimelineError(null)
      setTimelineLoading(false)
      return
    }

    let cancelled = false
    setTimelineLoading(true)
    setTimelineError(null)
    void supportCenterReportsService
      .listPatientHistory({ patient_cpf: patientCpfDigits })
      .then((rows) => {
        if (!cancelled) setTimeline(rows)
      })
      .catch((e) => {
        if (!cancelled) {
          setTimeline([])
          setTimelineError(
            e instanceof Error ? e.message : 'Não foi possível carregar o histórico do paciente.'
          )
        }
      })
      .finally(() => {
        if (!cancelled) setTimelineLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [patientCpfDigits])

  React.useEffect(() => {
    let cancelled = false
    setFormLoading(true)
    setFormError(null)
    setPsychAttendance(null)
    setAttendanceNotes(null)

    if (!formKind) {
      setFormLoading(false)
      return
    }

    if (formKind === 'psicologico') {
      void supportCenterAttendService
        .getPsychologicalAttendance(record.id)
        .then((row) => {
          if (!cancelled) setPsychAttendance(row)
        })
        .catch((e) => {
          if (!cancelled) {
            setPsychAttendance(null)
            setFormError(
              e instanceof Error ? e.message : 'Não foi possível carregar o prontuário.'
            )
          }
        })
        .finally(() => {
          if (!cancelled) setFormLoading(false)
        })
      return () => {
        cancelled = true
      }
    }

    if (isReducedTriageForm(formKind)) {
      void supportCenterAttendService
        .getAttendanceNotes(formKind, record.id)
        .then((row) => {
          if (!cancelled) setAttendanceNotes(row)
        })
        .catch((e) => {
          if (!cancelled) {
            setAttendanceNotes(null)
            setFormError(
              e instanceof Error ? e.message : 'Não foi possível carregar o atendimento.'
            )
          }
        })
        .finally(() => {
          if (!cancelled) setFormLoading(false)
        })
      return () => {
        cancelled = true
      }
    }

    setFormLoading(false)
    return () => {
      cancelled = true
    }
  }, [record.id, formKind])

  const selectTimelineItem = async (row: SupportCenterHistoryItem) => {
    if (row.appointment_id === record.id || recordLoading) return
    setRecordLoading(true)
    setRecordError(null)
    try {
      const next = await supportCenterReportsService.getAppointmentRecord(row.appointment_id)
      if (!next) {
        setRecordError('Agendamento não encontrado.')
        return
      }
      setRecord(next)
    } catch (e) {
      setRecordError(
        e instanceof Error ? e.message : 'Não foi possível carregar o agendamento.'
      )
    } finally {
      setRecordLoading(false)
    }
  }

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-record-modal-title"
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-6xl max-h-[92vh] flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="min-w-0">
            <Heading
              as="h2"
              size="2xl"
              id="appointment-record-modal-title"
              className="text-gray-900 dark:text-white"
            >
              Agendamento
            </Heading>
            <Text size="xs" color="muted" className="mt-0.5">
              {record.patient_name || 'Paciente'}
              {record.specialization_name ? ` · ${record.specialization_name}` : ''} ·{' '}
              {statusLabel}
            </Text>
          </div>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
          <aside className="md:w-72 lg:w-80 shrink-0 border-b md:border-b-0 md:border-r border-gray-200 dark:border-gray-700 flex flex-col max-h-[40vh] md:max-h-none">
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
              <Text
                size="xs"
                className="font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300"
              >
                Histórico do paciente
              </Text>
              {patientCpfDigits.length === 11 ? (
                <Text size="xs" color="muted" className="mt-0.5">
                  CPF {formatCpf(patientCpfDigits)}
                </Text>
              ) : null}
            </div>

            <div className="overflow-y-auto flex-1 min-h-0 px-3 py-3">
              {patientCpfDigits.length !== 11 ? (
                <Text size="sm" color="muted">
                  CPF do paciente indisponível para carregar o histórico.
                </Text>
              ) : timelineLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner />
                </div>
              ) : timelineError ? (
                <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                  {timelineError}
                </div>
              ) : timeline.length === 0 ? (
                <Text size="sm" color="muted">
                  Nenhum atendimento encontrado para este paciente.
                </Text>
              ) : (
                <ol className="border-l border-gray-200 dark:border-gray-700 ml-2 space-y-0">
                  {timeline.map((item) => {
                    const active = item.appointment_id === record.id
                    return (
                      <li key={item.appointment_id} className="relative ml-4 pb-4 last:pb-0">
                        <span
                          className={`absolute -left-[1.375rem] top-3 h-3 w-3 rounded-full border-2 ${
                            active
                              ? 'border-primary-600 bg-primary-600'
                              : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => void selectTimelineItem(item)}
                          disabled={recordLoading}
                          className={`w-full text-left rounded-lg px-3 py-2 transition-colors ${
                            active
                              ? 'bg-primary-50 dark:bg-primary-900/30 ring-1 ring-primary-200 dark:ring-primary-800'
                              : 'hover:bg-gray-50 dark:hover:bg-gray-800/80'
                          } disabled:opacity-60`}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {displayDate(item.scheduled_at)}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                            {item.specialization_name || 'Especialidade'}
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${appointmentStatusBadgeClass(
                                item.status
                              )}`}
                            >
                              {APPOINTMENT_STATUS_LABELS[item.status]}
                            </span>
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
                            {item.attendant_name || item.attendant_email || '—'}
                          </div>
                        </button>
                      </li>
                    )
                  })}
                </ol>
              )}
            </div>
          </aside>

          <div className="flex-1 min-w-0 overflow-y-auto px-6 py-5 relative">
            {recordLoading && (
              <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 dark:bg-gray-900/60">
                <LoadingSpinner />
              </div>
            )}

            {recordError && (
              <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm mb-4">
                {recordError}
              </div>
            )}

            <div className="space-y-8">
              <section className="space-y-3">
                <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
                  Formulário do atendimento
                </Heading>

                {formLoading ? (
                  <Text size="sm" color="muted">
                    Carregando formulário...
                  </Text>
                ) : formError ? (
                  <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
                    {formError}
                  </div>
                ) : isPsychological ? (
                  psychAttendance ? (
                    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                      <DetailField label="Tipo de demanda" value={attendanceDemandList} wide />
                      {SUPPORT_CENTER_PSYCH_VIEW_FIELDS.map((field) => {
                        const raw = psychAttendance[field.key]
                        const value =
                          field.key === 'next_contact_date' && raw ? formatDateBr(raw) : raw
                        const wide =
                          field.key === 'demand_notes' ||
                          field.key === 'intervention_notes' ||
                          field.key === 'patient_response' ||
                          field.key === 'additional_notes'
                        return (
                          <DetailField
                            key={field.key}
                            label={field.label}
                            value={display(value)}
                            wide={wide}
                          />
                        )
                      })}
                    </dl>
                  ) : (
                    <Text size="sm" color="muted">
                      Nenhum prontuário gravado para este atendimento.
                    </Text>
                  )
                ) : isReducedForm ? (
                  attendanceNotes &&
                  (attendanceNotes.notes?.trim() || attendanceDemandList !== '—') ? (
                    <dl className="grid grid-cols-1 gap-y-4">
                      <DetailField label="Tipo de demanda" value={attendanceDemandList} wide />
                      <DetailField label={notesLabel} value={attendanceNotes.notes} wide />
                    </dl>
                  ) : (
                    <Text size="sm" color="muted">
                      Nenhum dado de atendimento gravado
                      {notesLabel ? ` (${notesLabel.toLowerCase()})` : ''}.
                    </Text>
                  )
                ) : (
                  <Text size="sm" color="muted">
                    Este agendamento não possui formulário de atendimento vinculado.
                  </Text>
                )}
              </section>

              <DetailSection title="Consulta">
                <DetailField label="Data agendada" value={displayDate(record.scheduled_at)} />
                <DetailField label="Status" value={display(statusLabel)} />
                <DetailField
                  label="Canal de agendamento"
                  value={appointmentChannelLabel(record.channel)}
                />
                <DetailField label="Especialidade" value={display(record.specialization_name)} />
                <DetailField label="Profissional" value={display(record.professional_name)} />
              </DetailSection>

              <DetailSection title="Pessoas">
                <DetailField label="Nome do segurado" value={display(record.holder_name)} />
                <DetailField label="CPF do segurado" value={display(formatCpf(record.holder_cpf))} />
                <DetailField label="Nome do paciente" value={display(record.patient_name)} />
                <DetailField
                  label="CPF do paciente"
                  value={display(formatCpf(record.patient_cpf))}
                />
                <DetailField
                  label="Parentesco"
                  value={display(record.relationship_to_holder)}
                />
              </DetailSection>

              <DetailSection title="Cobertura">
                <DetailField label="Cobertura" value={display(coverage)} />
                <DetailField
                  label="Número da apólice"
                  value={display(record.policy_code != null ? String(record.policy_code) : null)}
                />
                <DetailField
                  label="Código do subgrupo"
                  value={display(
                    record.agreement_code != null ? String(record.agreement_code) : null
                  )}
                />
                <DetailField
                  label="Código do fornecedor"
                  value={display(
                    record.assistance_provider_code != null
                      ? String(record.assistance_provider_code)
                      : null
                  )}
                />
              </DetailSection>

              <DetailSection title="Contato">
                <DetailField
                  label="Telefone"
                  value={display(formatPhone(record.patient_phone) || null)}
                />
                <DetailField label="E-mails de contato" value={joinList(record.client_emails)} />
                <DetailField
                  label="E-mails internos"
                  value={joinList(record.internal_emails)}
                  wide
                />
              </DetailSection>

              <DetailSection title="Links e integração">
                <DetailField
                  label="Link da chamada"
                  value={
                    record.call_link ? (
                      <a
                        href={record.call_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 dark:text-primary-400 underline break-all"
                      >
                        {record.call_link}
                      </a>
                    ) : (
                      '—'
                    )
                  }
                  wide
                />
                <DetailField
                  label="Link encurtado"
                  value={
                    record.call_link_short ? (
                      <a
                        href={record.call_link_short}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary-600 dark:text-primary-400 underline break-all"
                      >
                        {record.call_link_short}
                      </a>
                    ) : (
                      '—'
                    )
                  }
                  wide
                />
                <DetailField
                  label="ID do evento Outlook"
                  value={display(record.outlook_event_id)}
                  wide
                />
              </DetailSection>

              <DetailSection title="Datas do atendimento">
                <DetailField
                  label="Confirmação solicitada em"
                  value={displayDate(record.confirmation_requested_at)}
                />
                <DetailField label="Confirmado em" value={displayDate(record.confirmed_at)} />
                <DetailField
                  label="Início do atendimento"
                  value={displayDate(record.attendance_started_at)}
                />
                <DetailField
                  label="Fim do atendimento"
                  value={displayDate(record.attendance_completed_at)}
                />
                <DetailField label="Criado em" value={displayDate(record.created_at)} />
                <DetailField label="Atualizado em" value={displayDate(record.updated_at)} />
              </DetailSection>

              <DetailSection title="Cancelamento e exclusão">
                <DetailField
                  label="Motivo do cancelamento"
                  value={display(record.cancel_reason_name)}
                  wide
                />
                <DetailField
                  label="Canal do cancelamento"
                  value={appointmentChannelLabel(record.cancellation_channel)}
                />
                <DetailField label="Excluído em" value={displayDate(record.deleted_at)} />
                <DetailField label="Excluído por" value={display(record.deleted_by_name)} />
              </DetailSection>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          <Button variant="solid" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
